import { Button, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useAuth } from '../../../../../hooks/useAuth';
import useDebounce from '../../../../../hooks/useDebounce';
import { CountUnitModel } from '../../../../../models/count-unit';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import {
  paginateCountUnits,
  searchCountUnitsByName,
} from '../../../../../store/features/metaData/metaDataSlice';
import { formatDate, UserPermissions } from '../../../../../util';
import { DrawerHashRoute } from '../../../../containers/Drawers/types';
import NoResultsFound from '../../../../elements/NoResultsFound';

import { ProductStatusEnum } from '../../../../../models/product-library';
import SearchBar from '../../../../elements/SearchBar/SearchBar';
import { RoutePath } from '../../../AppRoot/types';

const { Text } = Typography;

const CountUnitTab = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { hasPermission } = useAuth();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounce<string>(searchQuery, 500);

  const {
    allCountUnits,
    fetchingCountUnits,
    searchingCountUnits,
    count,
    currentPage,
    pageSize,
  } = useAppSelector((state) => state.metaData);

  const hasNoSearchResultsFound = useMemo(
    () => !fetchingCountUnits && !!searchQuery && allCountUnits.length === 0,
    [fetchingCountUnits, searchQuery, allCountUnits.length]
  );

  const hasCountUnits = !(!fetchingCountUnits && allCountUnits.length === 0);

  const columns: ColumnsType<CountUnitModel> = useMemo(
    () => getColumns(history, hasPermission),
    [history, hasPermission]
  );

  useEffect(() => {
    dispatch(
      searchCountUnitsByName({
        query: debouncedSearchQuery,
      })
    );
  }, [debouncedSearchQuery, dispatch]);

  const updateSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handlePagination = useCallback(
    (page: number, pageSize: number) => {
      dispatch(
        paginateCountUnits({
          page,
          pageSize,
          queryStr: debouncedSearchQuery,
        })
      );
    },
    [debouncedSearchQuery, dispatch]
  );

  const paginationSettings = {
    hideOnSinglePage: false,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    onChange: handlePagination,
    onShowSizeChange: handlePagination,
    showTotal: (total: number, range: number[]) =>
      `${range[0]}-${range[1]} of ${total} items`,
    style: {
      marginTop: '0px',
      padding: '2rem 1rem',
      background: 'white',
      borderRadius: '0 0 4px 4px',
    },
    total: count,
    current: currentPage,
    pageSize: pageSize,
  };

  return (
    <div>
      <SearchBar
        searchQuery={searchQuery}
        updateSearchQuery={updateSearchQuery}
        isSearching={searchingCountUnits}
        canCreate={hasPermission(UserPermissions.MetaDataCreate)}
        createLink={DrawerHashRoute.CountUnitForm}
        createButtonText="Add New Count Unit"
        placeholder="Search Count Units"
      />
      {hasCountUnits && hasNoSearchResultsFound ? (
        <NoResultsFound
          searchQuery={searchQuery}
          onClear={() => updateSearchQuery('')}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={allCountUnits}
          sortDirections={['ascend', 'descend', 'ascend']}
          pagination={paginationSettings}
          loading={fetchingCountUnits || searchingCountUnits}
        />
      )}
    </div>
  );
};

const getColumns = (
  history: any,
  hasPermission: (permission: UserPermissions | UserPermissions[]) => boolean
): ColumnsType<CountUnitModel> => [
  {
    title: 'Count Unit',
    dataIndex: 'name',
    width: '35%',
    sorter: (a: CountUnitModel, b: CountUnitModel) =>
      a.name.localeCompare(b.name),
  },
  {
    title: '# in Processing',
    dataIndex: 'in_processing',
    defaultSortOrder: 'descend',
    sorter: (a: CountUnitModel, b: CountUnitModel) =>
      (a.in_processing || 0) - (b.in_processing || 0),
  },
  {
    title: '# in Library',
    dataIndex: 'in_library',
    defaultSortOrder: 'descend',
    sorter: (a: CountUnitModel, b: CountUnitModel) =>
      (a.in_library || 0) - (b.in_library || 0),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    defaultSortOrder: 'descend',
    sorter: (a: CountUnitModel, b: CountUnitModel) =>
      a.status && b.status ? (a.status > b.status ? 1 : -1) : 0,
    render: (status: ProductStatusEnum) => (
      <Text
        type={
          status === ProductStatusEnum.needs_approval ? 'danger' : undefined
        }
      >
        {status === ProductStatusEnum.approved ? status : 'Needs Approval'}
      </Text>
    ),
  },
  {
    title: 'Created At',
    dataIndex: 'created_at',
    defaultSortOrder: 'descend',
    sorter: (a: CountUnitModel, b: CountUnitModel) =>
      new Date(a.created_at ?? 0).getTime() -
      new Date(b.created_at ?? 0).getTime(),
    render: (createdAt: string) => formatDate(createdAt),
  },
  {
    title: 'Actions',
    width: 100,
    render: (countUnit: CountUnitModel) => (
      <Space direction="horizontal" size="middle">
        {hasPermission(UserPermissions.MetaDataEdit) && (
          <Button
            type="link"
            className="link-button"
            onClick={() =>
              history.push({
                hash: DrawerHashRoute.CountUnitForm,
                state: { data: countUnit },
              })
            }
          >
            Edit
          </Button>
        )}
        <Button
          className="link-button"
          type="link"
          onClick={() =>
            history.push({
              state: { countUnit },
              pathname: RoutePath.ProductLibrary,
            })
          }
        >
          View in Library
        </Button>
      </Space>
    ),
  },
];

export default CountUnitTab;
